import React, { useEffect, useMemo, useState } from 'react';
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import KogKeyboardDatePickerContainer from '@/main/components/shared/datepicker/keyboardDatePickerContainer';
import {
  getTranslateByKey,
  hasAccessByRoute,
  isNullUndefined
} from '@/main/services/utils';
import { useNavigate } from 'react-router-dom';
import * as S from './styles';
import { RoutesConstants } from '@/main/routes/routesConstants';
import {
  getSystemApplication,
  useSystemApplicationReducer
} from '@/main/store/systemApplication';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { KeyValue, KeyValueEnum } from '@/main/models/common';
import { useDispatch } from 'react-redux';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import {
  finalDateForm,
  formatDateToISOString,
  initalDateForm,
  TypeKeysValidationsDate,
  validateFormDate
} from '@/main/services/utilsDate';
import {
  getDomainAnnouncements,
  getDomainCompanyAnnouncers,
  getDomainWhoCanSee,
  getStatusDomain,
  useAnnouncementReducer
} from '@/main/store/announcement';
import AnnouncementSearchGrid, {
  IGridData,
  INITIAL_GRID_STATE
} from './announcementSearchGrid';
import { AnnouncementFilterRequest } from '@/main/models/request/announcementRequest';
import { announcementApi } from '@/main/api';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';
import { AnnouncementStatusEnum } from '@/main/services/enum/AnnouncementStatusEnum';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { isNil } from 'lodash';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import { resetReducerCompany } from '@/main/store/company';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';

const ID_BASE_COMPONENTS = 'id-searchAnnouncements';

export function AnnouncementScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ignoreKeyValidations: TypeKeysValidationsDate[] = [
    'finalDateGreaterThanCurrentDate'
  ];

  const systemApplicationReducer = useSystemApplicationReducer();
  const announcementReducer = useAnnouncementReducer();
  const countryReducer = useCountryReducer();
  const companyTypeReducer = useCompanyTypeReducer();

  const defaultParameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSearchAnnouncementDaysInterval
  );
  const maxParameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.MaximumSearchAnnouncementDaysInterval
  );

  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [formFilters, setFormFilters] =
    useState<ISearchAnnouncementForm | null>(null);

  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);

  const INITIAL_FORM_STATE = {
    name: null as KeyValue | null,
    systemApplication: null as KeyValue | null,
    whoCanSee: [] as KeyValue[] | null,
    startDate: initalDateForm(),
    finalDate: finalDateForm(defaultParameterRangeDays),
    announcementStatus: null as KeyValueEnum | null,
    company: null as KeyValue | null,
    country: null as KeyValue | null,
    companyType: null as KeyValue | null
  };

  type ISearchAnnouncementForm = typeof INITIAL_FORM_STATE;

  const {
    control,
    setValue,
    customHandleSubmit,
    reset,
    clearErrors,
    watch,
    formState: { errors }
  } = useFormQueryParams<ISearchAnnouncementForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmit,
    mapDefaultValues: {
      systemApplication: systemApplicationReducer.systemApplication,
      announcementStatus: announcementReducer.statusDomain,
      whoCanSee: announcementReducer.domainWhoCanSee,
      name: announcementReducer.domain,
      company: announcementReducer.domainCompanyAnnouncers
    } as any
  });

  const watcherCompany = watch('company');
  const watcherCompanyType = watch('companyType');
  const watcherCountry = watch('country');

  useEffect(() => {
    dispatch(getSystemApplication());
    dispatch(getStatusDomain(true));

    dispatch(getDomainAnnouncements());
    dispatch(getDomainWhoCanSee());
    dispatch(getCountriesUser());
    dispatch(getCompanyType());

    return () => {
      dispatch(resetReducerCompany());
    };
  }, []);

  useEffect(() => {
    if (!isNil(watcherCompany) && !announcementReducer.domain.isLoading) {
      dispatch(getDomainAnnouncements(watcherCompany.key.toString()));
    }
  }, [watcherCompany]);

  useEffect(() => {
    if (
      !isNil(watcherCountry) &&
      !announcementReducer.domainCompanyAnnouncers.isLoading
    ) {
      dispatch(
        getDomainCompanyAnnouncers({
          guidCountry: watcherCountry.key.toString(),
          idCompanyType: !isNil(watcherCompanyType)
            ? (watcherCompanyType.key as number)
            : undefined
        })
      );
    }
  }, [watcherCountry, watcherCompanyType]);

  const optionWhoCanSee = useMemo(() => {
    return (
      announcementReducer.domainWhoCanSee.data?.map(item => ({
        key: item.key,
        value: item.value
      })) || []
    );
  }, [
    announcementReducer.domainWhoCanSee.data,
    announcementReducer.domainWhoCanSee.isLoading
  ]);

  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
    countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;

  const isDisableSearchButton = () =>
    systemApplicationReducer.systemApplication.isLoading ||
    announcementReducer.statusDomain.isLoading ||
    announcementReducer.domain.isLoading ||
    announcementReducer.domainCompanyAnnouncers.isLoading ||
    announcementReducer.domainWhoCanSee.isLoading;

  const disabledDropAnnouments = useMemo(
    () => isNil(watcherCompany) || isNil(watcherCompanyType),
    [watcherCompany, watcherCompanyType]
  );

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterAnnouncement);
  }

  function isDisabledCountry() {
    return countryReducer.countriesUser.data?.length === 1;
  }

  function onSubmit(formInputs: ISearchAnnouncementForm) {
    validateFormDate(
      formInputs.startDate,
      formInputs.finalDate,
      maxParameterRangeDays,
      ignoreKeyValidations
    );

    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });
    setFormFilters(formInputs);

    const request: AnnouncementFilterRequest = {
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30,
      guid: formInputs.name ? String(formInputs.name.key) : undefined,
      idSystemApplication: formInputs.systemApplication
        ? Number(formInputs.systemApplication.key)
        : undefined,
      guidCompany: formInputs.company
        ? (formInputs.company.key as string)
        : undefined,
      guidCountry: formInputs.country
        ? (formInputs.country.key as string)
        : undefined,
      idAnnouncementStatus: formInputs.announcementStatus
        ? Number(formInputs.announcementStatus.key)
        : undefined,
      idCompanyType: formInputs.companyType
        ? Number(formInputs.companyType.key)
        : undefined,
      startDate: formatDateToISOString(formInputs.startDate),
      endDate: formatDateToISOString(formInputs.finalDate),
      whoCanSeeCompanyTypeIds:
        formInputs.whoCanSee && formInputs.whoCanSee.length > 0
          ? formInputs.whoCanSee.map(item => Number(item.key))
          : []
    };

    setGridData(crr => ({ ...crr, isLoading: true }));

    announcementApi
      .getAnnouncements(request)
      .then(response => {
        let resultData: any[] = [];
        let resultTotalItems: number = 0;
        if (response.data) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });
      })
      .finally(() => setGridData(crr => ({ ...crr, isLoading: false })));
  }

  function onReset() {
    reset(INITIAL_FORM_STATE);

    const optionValueSystemApplication =
      systemApplicationReducer.systemApplication.data?.find(
        x => x.key == String(SystemApplicationEnum.Claims)
      );

    setValue('startDate', initalDateForm());
    setValue('finalDate', finalDateForm(defaultParameterRangeDays));
    setValue('name', null);
    setValue('whoCanSee', null);
    setValue('announcementStatus', null);
    setValue('company', null);
    setValue('country', optionDefaultCountryDrop);
    setValue('systemApplication', optionValueSystemApplication ?? null);

    if (companyTypeReducerFilter.length === 1) {
      setValue('companyType', companyTypeReducerFilter[0]);
    }

    setFormFilters(null);
    setGridData(INITIAL_GRID_STATE);

    clearErrors();
  }

  const allowedTypes = new Set<CompanyTypeEnum>();

  if (userIsAdmin()) {
    allowedTypes.add(CompanyTypeEnum.Admin);
    allowedTypes.add(CompanyTypeEnum.Insurer);
  } else if (userIsInsurer()) {
    allowedTypes.add(CompanyTypeEnum.Insurer);
  }

  const companyTypeReducerFilter = useMemo(() => {
    return (
      companyTypeReducer.companyType.data?.filter(item =>
        allowedTypes.has(Number(item.key) as CompanyTypeEnum)
      ) ?? []
    );
  }, [companyTypeReducer.companyType.data]);

  return (
    <ContainerScreen
      hasBackground
      title={`
      ${getTranslateByKey('information.registration')}
      ${' > '}
      ${getTranslateByKey('information.searchLoginAnnouncements')}`}>
      <S.Container>
        <AreaButton>
          <Button
            onClick={handleFilters}
            id={`${ID_BASE_COMPONENTS}-button-filters`}>
            {getTranslateByKey('information.filters')}
          </Button>
          {hasAccessByRoute(
            RoutesConstants.registrationRegisterAnnouncement
          ) && (
            <Button
              onClick={handleNew}
              id={`${ID_BASE_COMPONENTS}-button-newAnnouncement`}>
              {getTranslateByKey('information.newAnnouncement')}
            </Button>
          )}
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.country')}
                control={control}
                nameCustom="country"
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                hasErrorCustom={!!errors.country}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                onChangeCustom={() => {}}
                disabled={isDisabledCountry()}
                autoHighlightCustom={
                  countryReducer.countriesUser.data?.length === 1
                }
                setValueCustom={setValue}
                id={`${ID_BASE_COMPONENTS}-select-country`}
              />

              <KogAutoCompleteControlled
                id={`${ID_BASE_COMPONENTS}-select-companyType`}
                nameCustom="companyType"
                label={getTranslateByKey('information.companyType')}
                isDefaultValueUnique
                control={control}
                setValueCustom={setValue}
                options={companyTypeReducerFilter}
                isLoading={companyTypeReducer.companyType.isLoading}
                rulesCustom={{ required: true }}
                disabled={companyTypeReducerFilter.length <= 1}
                onChangeCustom={() => {
                  setValue('company', null);
                  setValue('name', null);
                }}
              />

              <KogAutoCompleteControlled
                label={getTranslateByKey('information.company')}
                isDefaultValueUnique
                control={control}
                nameCustom="company"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={announcementReducer.domainCompanyAnnouncers.data}
                disabled={announcementReducer.domainCompanyAnnouncers.data?.length === 1}
                isLoading={
                  announcementReducer.domainCompanyAnnouncers.isLoading
                }
                setValueCustom={setValue}
                id={`${ID_BASE_COMPONENTS}-select-company`}
                onChangeCustom={() => {}}
              />

              <KogAutoCompleteControlled
                id={`${ID_BASE_COMPONENTS}-select-system`}
                label={getTranslateByKey('information.systemApplication')}
                control={control}
                nameCustom="systemApplication"
                rulesCustom={{ required: true }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={systemApplicationReducer.systemApplication.data}
                isLoading={systemApplicationReducer.systemApplication.isLoading}
                onChangeCustom={() => {}}
                setValueCustom={setValue}
              />

              <KogKeyboardDatePickerContainer
                idScreen={`${ID_BASE_COMPONENTS}-datePicker-startDate-finalDate`}
                nameStartDate="startDate"
                nameFinalDate="finalDate"
                control={control}
                ignoreKeyValidations={ignoreKeyValidations}
                parameterRangeDays={maxParameterRangeDays}
              />

              <KogAutoCompleteControlled
                id={`${ID_BASE_COMPONENTS}-select-announcementName`}
                label={getTranslateByKey('information.name')}
                control={control}
                nameCustom="name"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={announcementReducer.domain.data}
                isLoading={announcementReducer.domain.isLoading}
                onChangeCustom={() => {}}
                setValueCustom={setValue}
                disabled={disabledDropAnnouments}
              />

              <KogInputAutoCompleteCheckControlled
                label={getTranslateByKey('information.whoCanSee')}
                name="whoCanSee"
                control={control}
                setValueCustom={setValue}
                options={optionWhoCanSee}
                isLoading={announcementReducer.domainWhoCanSee.isLoading}
                onChangeCustom={() => {}}
                id={`${ID_BASE_COMPONENTS}-txt-whoCanSee`}
                disableSelectAll
              />

              <KogAutoCompleteControlled
                id={`${ID_BASE_COMPONENTS}-txt-announcementStatus`}
                label={getTranslateByKey('information.status')}
                valueDefaultKey={String(AnnouncementStatusEnum.Active)}
                control={control}
                nameCustom="announcementStatus"
                rulesCustom={{ required: true }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={announcementReducer.statusDomain.data}
                isLoading={announcementReducer.statusDomain.isLoading}
                onChangeCustom={() => {}}
                setValueCustom={setValue}
              />
            </S.RowInputs>

            <KOGAreaButtons>
              <SecondaryButton
                id={`${ID_BASE_COMPONENTS}-button-search`}
                type="submit"
                disabled={isDisableSearchButton()}>
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                id={`${ID_BASE_COMPONENTS}-button-clear`}
                onClick={onReset}>
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>

        <AnnouncementSearchGrid
          formFilters={formFilters}
          gridData={gridData}
          setGridData={setGridData}
        />
      </S.Container>
    </ContainerScreen>
  );
}
